import Mustache from "mustache";

import type { HttpRequestSpec, Kv, PostgresRequestSpec, RequestSpec } from "@moment/api-collab";

import type { EnvironmentVars } from "../environments/environments.types";

// Disable HTML escaping - we're not rendering HTML, just substituting values
Mustache.escape = (text: string) => text;

export type EnvVarHint = "string" | "kubecontext";

export type EnvVarReferenceWithHint = {
	name: string;
	hint: EnvVarHint;
};

export type CallParams = Record<string, string>;

export type MomentVars = {
	auth_token?: string;
};

/**
 * Finds all variable references ({{env.X}} or {{params.X}}) in a string that are not resolved.
 * Uses Mustache.parse to extract variable tokens.
 */
export const findUnresolvedInString = (
	str: string,
	envVars: EnvironmentVars,
	params: CallParams = {},
	momentVars: MomentVars = {}
): string[] => {
	const unresolved: string[] = [];
	const tokens = Mustache.parse(str);

	for (const token of tokens) {
		const tokenType = token[0];

		// "text" = literal text, always allowed
		if (tokenType === "text") {
			continue;
		}

		// "name" = {{var}} - the only supported variable syntax
		if (tokenType === "name") {
			const varPath = token[1];
			const parts = varPath.split(".");

			// Handle env._moment.* variables (3 parts)
			if (parts.length === 3 && parts[0] === "env" && parts[1] === "_moment") {
				const varName = parts[2];
				if (varName === "auth_token") {
					if (momentVars.auth_token === undefined) {
						unresolved.push(varPath);
					}
				} else {
					unresolved.push(varPath);
				}
				continue;
			}

			if (parts.length !== 2) {
				// Must be exactly namespace.varName (no dots in variable names)
				unresolved.push(varPath);
				continue;
			}

			const [namespace, varName] = parts;

			if (!varName) {
				// Empty variable name (like {{env.}})
				unresolved.push(varPath);
			} else if (namespace === "env") {
				if (!(varName in envVars)) {
					unresolved.push(varPath);
				}
			} else if (namespace === "params") {
				if (!(varName in params)) {
					unresolved.push(varPath);
				}
			} else if (namespace === "vars") {
				// {{vars.X}} is an alias for {{params.X}} — same substitution map.
				// Use {{vars.X}} in HTTP requests to inject caller-supplied values
				// without conflicting with the URL query-string params Kv[].
				if (!(varName in params)) {
					unresolved.push(varPath);
				}
			} else {
				unresolved.push(varPath);
			}
			continue;
		}

		// Reject unsupported Mustache syntax (sections, partials, comments, etc.)
		unresolved.push(`unsupported syntax: {{${token[1]}}}`);
	}

	return unresolved;
};

/**
 * Visits every user-facing string field in a RequestSpec, calling `cb` for each
 * non-empty value. Skips disabled Kv entries. This is the single place that knows
 * the shape of each spec variant so callers don't have to duplicate the traversal.
 */
const forEachRequestString = (
	request: RequestSpec,
	cb: (str: string, hint: EnvVarHint) => void
): void => {
	const visit = (str: string | undefined, hint: EnvVarHint = "string") => {
		if (str) {
			cb(str, hint);
		}
	};

	const visitKvArray = (arr: Kv[] | undefined) => {
		if (!arr) {
			return;
		}
		for (const kv of arr) {
			if (kv?.enabled !== false) {
				visit(kv.key);
				visit(kv.value);
			}
		}
	};

	if (request.type === "postgres") {
		// Postgres spec fields
		visit(request.connectionString);
		visit(request.sql);
		visitKvArray(request.params);
		return;
	}

	if (request.type === "sqlite") {
		visit(request.filePath);
		visit(request.sql);
		visitKvArray(request.params);
		return;
	}

	if (request.type === "javascript") {
		visit(request.code);
		visitKvArray(request.params);
		return;
	}

	if (request.type === "grpc") {
		visit(request.url);
		visit(request.message);
		visitKvArray(request.metadata);
		visitKvArray(request.vars);
		if (request.auth) {
			switch (request.auth.type) {
				case "basic":
				case "digest":
					visit(request.auth.username);
					visit(request.auth.password);
					break;
				case "bearer":
					visit(request.auth.token);
					break;
				case "oauth2":
					visit(request.auth.accessToken);
					visit(request.auth.refreshToken);
					visit(request.auth.tokenUrl);
					visit(request.auth.clientId);
					visit(request.auth.clientSecret);
					visit(request.auth.scope);
					break;
				case "none":
				default:
					break;
			}
		}
		return;
	}

	// HTTP spec fields
	// URL
	visit(request.url);

	// Params
	visitKvArray(request.params);

	// Headers
	visitKvArray(request.headers);

	// Body
	if (request.body) {
		visit(request.body.text);
		visitKvArray(request.body.form);
	}

	// Auth (type-specific fields)
	if (request.auth) {
		switch (request.auth.type) {
			case "basic":
			case "digest":
				visit(request.auth.username);
				visit(request.auth.password);
				break;
			case "bearer":
				visit(request.auth.token);
				break;
			case "oauth2":
				visit(request.auth.accessToken);
				visit(request.auth.refreshToken);
				visit(request.auth.tokenUrl);
				visit(request.auth.clientId);
				visit(request.auth.clientSecret);
				visit(request.auth.scope);
				break;
			case "kubernetes":
				visit(request.auth.context, "kubecontext");
				break;
			case "none":
			default:
				break;
		}
	}
};

/**
 * Finds all unresolved variable references in a RequestSpec (HTTP or Postgres).
 * Returns a deduplicated list of variable paths not found in the environment or params.
 */
export const findUnresolvedVariables = (
	request: RequestSpec,
	envVars: EnvironmentVars,
	params: CallParams = {},
	momentVars: MomentVars = {}
): string[] => {
	const unresolved = new Set<string>();

	forEachRequestString(request, (str) => {
		for (const varPath of findUnresolvedInString(str, envVars, params, momentVars)) {
			unresolved.add(varPath);
		}
	});

	return Array.from(unresolved);
};

/**
 * Extracts all {{env.X}} variable names referenced in a string (regardless of resolution).
 * Ignores {{params.*}}, {{env._moment.*}}, and unsupported syntax.
 * Returns variable names (the "X" part, not "env.X"). May contain duplicates.
 */
export const findEnvVarReferencesInString = (str: string): string[] => {
	let tokens: ReturnType<typeof Mustache.parse>;
	try {
		tokens = Mustache.parse(str);
	} catch {
		// Malformed Mustache template — return empty rather than crashing the scan
		return [];
	}

	const refs: string[] = [];

	for (const token of tokens) {
		if (token[0] !== "name") {
			continue;
		}

		const varPath = token[1];
		const parts = varPath.split(".");

		// Skip env._moment.* (internal vars, not user-supplied)
		if (parts.length === 3 && parts[0] === "env" && parts[1] === "_moment") {
			continue;
		}

		if (parts.length === 2 && parts[0] === "env" && parts[1]) {
			refs.push(parts[1]);
		}
	}

	return refs;
};

/**
 * Extracts all {{env.X}} variable names referenced in a RequestSpec.
 * Returns a deduplicated list of variable names (just the "X" part).
 * Ignores {{params.*}}, {{env._moment.*}}, and unsupported syntax.
 */
export const findEnvVarReferences = (request: RequestSpec): string[] => {
	const refs = new Set<string>();

	forEachRequestString(request, (str) => {
		for (const varName of findEnvVarReferencesInString(str)) {
			refs.add(varName);
		}
	});

	return Array.from(refs);
};

/**
 * Extracts all {{env.X}} variable references in a RequestSpec with semantic hints.
 * Uses forEachRequestString for traversal — hint "kubecontext" comes from auth.context
 * when auth.type === "kubernetes", everything else is "string".
 * Returns a deduplicated list (by variable name); most specific hint wins.
 */
export const findEnvVarReferencesWithHints = (request: RequestSpec): EnvVarReferenceWithHint[] => {
	const hints: Map<string, EnvVarHint> = new Map();

	forEachRequestString(request, (str, hint) => {
		for (const name of findEnvVarReferencesInString(str)) {
			const existing = hints.get(name);
			if (!existing || existing === "string") {
				hints.set(name, hint);
			}
		}
	});

	const result: EnvVarReferenceWithHint[] = [];
	hints.forEach((hint, name) => {
		result.push({ name, hint });
	});
	return result;
};

/**
 * Substitutes all {{env.X}} and {{params.X}} references in a string with values.
 * Uses Mustache for template rendering.
 * Throws an error if any variables are unresolved.
 */
export const substituteString = (
	str: string,
	envVars: EnvironmentVars,
	params: CallParams = {},
	momentVars: MomentVars = {}
): string => {
	const unresolved = findUnresolvedInString(str, envVars, params, momentVars);
	if (unresolved.length > 0) {
		throw new Error(`Unresolved variables: ${unresolved.join(", ")}`);
	}
	// `vars` is an alias for `params` in the template context — both namespaces
	// resolve from the same caller-supplied values.
	return Mustache.render(str, { env: { ...envVars, _moment: momentVars }, params, vars: params });
};

/**
 * Substitutes all {{env.X}} and {{params.X}} references in a RequestSpec.
 * Returns a new RequestSpec with all variables resolved.
 */
export const substituteVariables = (
	request: RequestSpec,
	envVars: EnvironmentVars,
	params: CallParams = {},
	momentVars: MomentVars = {}
): RequestSpec => {
	const sub = (str: string | undefined): string => {
		if (!str) {
			return str ?? "";
		}
		return substituteString(str, envVars, params, momentVars);
	};

	const subKvArray = (arr: Kv[] | undefined): Kv[] => {
		if (!arr) {
			return [];
		}
		return arr.map((kv) => {
			// Skip substitution for disabled kvs (matches findUnresolvedVariables behavior)
			if (kv?.enabled === false) {
				return kv;
			}
			return {
				...kv,
				key: sub(kv.key),
				value: sub(kv.value),
			};
		});
	};

	if (request.type === "postgres") {
		return {
			...request,
			connectionString: sub(request.connectionString),
			sql: sub(request.sql),
			params: subKvArray(request.params),
		};
	}

	if (request.type === "sqlite") {
		return {
			...request,
			filePath: sub(request.filePath),
			sql: sub(request.sql),
			params: subKvArray(request.params),
		};
	}

	if (request.type === "javascript") {
		return {
			...request,
			code: sub(request.code),
			params: subKvArray(request.params),
		};
	}

	const substituteAuth = (auth: HttpRequestSpec["auth"]): HttpRequestSpec["auth"] => {
		if (!auth) {
			return auth;
		}
		switch (auth.type) {
			case "basic":
				return {
					...auth,
					username: auth.username ? sub(auth.username) : undefined,
					password: auth.password ? sub(auth.password) : undefined,
				};
			case "digest":
				return {
					...auth,
					username: auth.username ? sub(auth.username) : undefined,
					password: auth.password ? sub(auth.password) : undefined,
				};
			case "bearer":
				return {
					...auth,
					token: auth.token ? sub(auth.token) : undefined,
				};
			case "oauth2":
				return {
					...auth,
					accessToken: auth.accessToken ? sub(auth.accessToken) : undefined,
					refreshToken: auth.refreshToken ? sub(auth.refreshToken) : undefined,
					tokenUrl: auth.tokenUrl ? sub(auth.tokenUrl) : undefined,
					clientId: auth.clientId ? sub(auth.clientId) : undefined,
					clientSecret: auth.clientSecret ? sub(auth.clientSecret) : undefined,
					scope: auth.scope ? sub(auth.scope) : undefined,
				};
			case "kubernetes":
				return {
					...auth,
					context: auth.context ? sub(auth.context) : undefined,
				};
			case "none":
			default:
				return auth;
		}
	};

	if (request.type === "grpc") {
		return {
			...request,
			url: sub(request.url),
			message: sub(request.message),
			metadata: subKvArray(request.metadata),
			vars: subKvArray(request.vars),
			auth: substituteAuth(request.auth),
		};
	}

	// HTTP spec substitution
	return {
		...request,
		url: sub(request.url),
		params: subKvArray(request.params),
		headers: subKvArray(request.headers),
		body: request.body
			? {
					...request.body,
					text: sub(request.body.text),
					form: subKvArray(request.body.form),
				}
			: request.body,
		auth: substituteAuth(request.auth),
	};
};

/**
 * Walks every user-editable string in a RequestSpec and returns a new spec
 * with each string mapped through `fn`. Disabled `Kv` entries are passed
 * through unchanged (same policy as `forEachRequestString`). Used by the
 * convert-to-secret / convert-to-local flows to rewrite refs across all of a
 * document's request pages.
 */
export const mapRequestSpecStrings = (
	request: RequestSpec,
	fn: (str: string) => string
): RequestSpec => {
	const apply = (str: string | undefined): string => (str === undefined ? str! : fn(str));

	const applyKvArray = (arr: Kv[] | undefined): Kv[] | undefined => {
		if (!arr) {
			return arr;
		}
		return arr.map((kv) => {
			if (kv?.enabled === false) {
				return kv;
			}
			return { ...kv, key: apply(kv.key), value: apply(kv.value) };
		});
	};

	if (request.type === "postgres") {
		return {
			...request,
			connectionString: apply(request.connectionString),
			sql: apply(request.sql),
			params: applyKvArray(request.params) ?? [],
		};
	}

	if (request.type === "sqlite") {
		return {
			...request,
			filePath: apply(request.filePath),
			sql: apply(request.sql),
			params: applyKvArray(request.params) ?? [],
		};
	}

	if (request.type === "javascript") {
		return {
			...request,
			code: apply(request.code),
			params: applyKvArray(request.params) ?? [],
		};
	}

	const applyAuth = (auth: HttpRequestSpec["auth"]): HttpRequestSpec["auth"] => {
		if (!auth) {
			return auth;
		}
		switch (auth.type) {
			case "basic":
			case "digest":
				return {
					...auth,
					username: auth.username ? apply(auth.username) : undefined,
					password: auth.password ? apply(auth.password) : undefined,
				};
			case "bearer":
				return { ...auth, token: auth.token ? apply(auth.token) : undefined };
			case "oauth2":
				return {
					...auth,
					accessToken: auth.accessToken ? apply(auth.accessToken) : undefined,
					refreshToken: auth.refreshToken ? apply(auth.refreshToken) : undefined,
					tokenUrl: auth.tokenUrl ? apply(auth.tokenUrl) : undefined,
					clientId: auth.clientId ? apply(auth.clientId) : undefined,
					clientSecret: auth.clientSecret ? apply(auth.clientSecret) : undefined,
					scope: auth.scope ? apply(auth.scope) : undefined,
				};
			case "kubernetes":
				return { ...auth, context: auth.context ? apply(auth.context) : undefined };
			case "none":
			default:
				return auth;
		}
	};

	if (request.type === "grpc") {
		return {
			...request,
			url: apply(request.url),
			message: apply(request.message),
			metadata: applyKvArray(request.metadata) ?? [],
			vars: applyKvArray(request.vars) ?? [],
			auth: applyAuth(request.auth),
		};
	}

	return {
		...request,
		url: apply(request.url),
		params: applyKvArray(request.params) ?? [],
		headers: applyKvArray(request.headers) ?? [],
		body: request.body
			? {
					...request.body,
					text: apply(request.body.text),
					form: applyKvArray(request.body.form) ?? [],
				}
			: request.body,
		auth: applyAuth(request.auth),
	};
};
